<template>
  <div
    v-if="showFilters || enableOnlySearchSection"
    class="table-filters">
    <template v-if="enableSearchSection || enableOnlySearchSection">
      <div
        v-if="columnsFiltrableByText.length > 0"
        class="input-container"
        :class="{ 'is-filter-on': filterText != null && filterText.trim() != '' }">
        <div class="icon">
          <IconSearch />
        </div>
        <input
          v-model="filterText"
          name="filterText"
          type="text"
          class="form-control table-text-filter"
          :placeholder="capitalize($t('messages.containsText'))"
          maxlength="191"
          @keyup="() => fireFiltersChanged()" />
        <div class="delete">
          <div
            v-if="filterText != null && filterText.length > 0"
            @click="clearFilterText">
            <IconCircleX />
          </div>
        </div>
      </div>
    </template>

    <ViewDropdown
      v-if="enableViews && !enableOnlySearchSection"
      :views="views"
      :can-save="canSaveFilters"
      @show-view-list="() => (showUserViewListModal = true)"
      @cleared="onViewCleared"
      @saved="onViewSaveClicked"
      @view-selected="applyView"
      @show-import-view="importView" />

    <DropDown
      v-if="availableFilters.length > 0 && useFiltersDropDown"
      ref="filtersDropdownRef"
      class="available-filters-drop-down"
      :options="availableFilters"
      option-label-property="label"
      option-track-by-property="property"
      option-disabled-property="isDisabled"
      :selected-options="selectedFilters"
      :select-all-by-default="false"
      :show-select-all="false"
      :label="`+ ${capitalize(i18n.t('names.filter', { n: 2 }))}`"
      :show-floating-label="false"
      :show-selections-in-label="false"
      :mark-as-having-selections-when-all-are-selected="true"
      :return-empty-selected-options-when-all-is-selected="false"
      @cleared="onFiltersCleared"
      @selection-changed="onFilterSelectionChanged" />

    <div
      v-for="filter in activeFilters"
      :key="`filter-${filter.instance.property}`"
      class="table-filter">
      <TableFilter
        ref="tableFilter"
        :class="`table-filter-${filter.instance.property}`"
        :filter="filter.instance"
        :filter-options-transformation="filter.filterOptionsTransformation"
        :is-disabled="filter.isDisabled"
        @filter-changed="onTableFilterChanged"
        @removed="removeFilter" />
    </div>

    <label
      v-if="allowComparison"
      class="flex-h">
      <CheckBox
        :checked="showComparison"
        :use-for-exclusion="false"
        :is-read-only="false"
        @click="toggleComparisonPreviousPeriod" />
      <span>{{ $t('messages.compareToPreviousPeriod') }}</span>
    </label>
    <div class="spacer" />
    <div class="table-actions">
      <slot name="actions" />
      <slot name="extra-toolbar" />
    </div>
    <slot name="extra-controls" />
  </div>

  <ViewListModal
    v-if="showUserViewListModal"
    :available-filters="availableFilters"
    :table-settings="tableSettings"
    @close="() => (showUserViewListModal = false)"
    @apply-view="applyView" />

  <AddView
    v-if="showAddUserViewModal"
    @save="saveView"
    @close="resetAddViewModalParams" />

  <UpdateViewModal
    v-if="showUpdateUserViewModal"
    :view-name="appliedView?.name"
    @save="updateView"
    @save-as-new="saveAsNewFilter"
    @close="() => (showUpdateUserViewModal = false)" />

  <ViewImportModal
    ref="viewImportModalRef"
    :table-settings="tableSettings"
    :available-filters="availableFilters" />
</template>

<script lang="ts" setup>
  import {
    capitalize,
    computed,
    onBeforeMount,
    onBeforeUnmount,
    onMounted,
    onUnmounted,
    Ref,
    ref,
    useTemplateRef,
    watch,
  } from 'vue';
  import { ComponentExposed } from 'vue-component-type-helpers';
  import { useI18n } from 'vue-i18n';
  import { useRouter } from 'vue-router';

  import GoogleAnalyticsApi from '@/common/apis/GoogleAnalyticsApi';
  import CheckBox from '@/common/components/controls/CheckBox.vue';
  import DropDown from '@/common/components/controls/dropdown/DropDown.vue';
  import TableColumn from '@/common/components/controls/table/TableColumn';
  import TableColumnTypes from '@/common/components/controls/table/TableColumnTypes';
  import { ITableFilter } from '@/common/components/controls/table/TableFilter';
  import { TableFilterType } from '@/common/components/controls/table/TableFilter.type';
  import TableFilter from '@/common/components/controls/table/TableFilter.vue';
  import TableSettings from '@/common/components/controls/table/TableUserSettings/TableSettings';
  import UserView from '@/common/components/controls/table/TableUserSettings/UserView';
  import AddView from '@/common/components/controls/table/user-views/AddView.vue';
  import UpdateViewModal from '@/common/components/controls/table/user-views/UpdateViewModal.vue';
  import ViewDropdown from '@/common/components/controls/table/user-views/ViewDropdown.vue';
  import ViewImportModal from '@/common/components/controls/table/user-views/ViewImportModal.vue';
  import ViewListModal from '@/common/components/controls/table/user-views/ViewListModal.vue';
  import { IconCircleX, IconSearch } from '@/common/icons/monochrome';
  import {
    Filter,
    FilterCollection,
    FilterComparisonOperator,
    FilterGroup,
    FilterLogicalOperator,
  } from '@/common/models';
  import Exception from '@/common/models/Exception';
  import BooleanFilter from '@/common/models/query-params/BooleanFilter';
  import DateFilter from '@/common/models/query-params/DateFilter';
  import ListFilter from '@/common/models/query-params/ListFilter';
  import NumericFilter from '@/common/models/query-params/NumericFilter';
  import UUIDFilter from '@/common/models/query-params/UUIDFilter';
  import GlobalEventBus from '@/GlobalEventBus';
  import GlobalEvents from '@/GlobalEvents';
  import { areArraysEqual, clearTooltips, orderBy } from '@/globals';
  import { waitUntil } from '@/utils/DelayHelper';
  import ThrottledTaskRunner from '@/utils/ThrottledTaskRunner';

  const i18n = useI18n();
  const router = useRouter();

  const lightVerticalBar = '❘'; // This a unicode character equivalent to the "|" symbol

  // #region Props
  interface IProps {
    columns: Record<string, TableColumn>;
    tableSettings: TableSettings;
    enableSearchSection?: boolean;
    enableViews?: boolean;
    category?: string;
    showHeaders?: boolean;
    showSaveBtn?: boolean;
    showFilters?: boolean;
    useFiltersDropDown?: boolean;
    allowComparison?: boolean;
    isMainTable?: boolean;
    fixedFilters?: Filter[];
    textSearchProperties?: string[];
    textSearch?: string;
    enableOnlySearchSection?: boolean;
  }

  const {
    columns,
    category,
    tableSettings,
    fixedFilters,
    isMainTable,
    textSearchProperties,
    showHeaders,
    showSaveBtn,
    ...props
  } = defineProps<IProps>();
  // #endregion

  // #region Emits
  interface IEmits {
    (event: 'toggleComparison', value: boolean): void;
    (event: 'filtersChanged'): void;
    (event: 'textSearchTermChanged', value: string): void;
  }

  const emits = defineEmits<IEmits>();
  // #endregion

  // #region Template Refs
  const filtersDropdownRef = useTemplateRef('filtersDropdownRef');
  const tableFilterRefs = useTemplateRef<ComponentExposed<typeof TableFilter>[]>('tableFilter');
  const viewImportModalRef = useTemplateRef<ComponentExposed<typeof ViewImportModal>>('viewImportModalRef');

  // #endregion

  // #region Data Refs
  const activeFilters: Ref<ITableFilter[]> = ref([]);
  const availableFilters: Ref<ITableFilter[]> = ref([]);
  const selectedFilters: Ref<ITableFilter[]> = ref([]);
  const didActiveFiltersChange: Ref<boolean> = ref(false);
  const showComparison: Ref<boolean> = ref(true);
  const filterText: Ref<string> = ref(props.textSearch);
  // Views
  const views = ref<UserView[]>();
  const showAddUserViewModal = ref<boolean>(false);
  const showUpdateUserViewModal = ref<boolean>(false);
  const showUserViewListModal = ref<boolean>(false);
  // #endregion

  // #region Computed
  const columnsAvailableForFiltering = computed(() => {
    return Object.values(columns).filter((c) => c.useAsFilter !== false && c.property != null);
  });

  const columnsFiltrableByText = computed(() => {
    return columnsAvailableForFiltering.value.filter(
      (x) =>
        [
          TableColumnTypes.string,
          TableColumnTypes.integer,
          TableColumnTypes.currency,
          TableColumnTypes.float,
          TableColumnTypes.percentage,
        ].includes(x.type) && x.source == null,
    );
  });

  const isFiltered = computed(() => {
    const hasTextSearch = filterText.value != null && filterText.value.trim() !== '';
    if (hasTextSearch) {
      return true;
    }
    const filterCollection = getFilters();
    const filters = filterCollection.filters as TableFilterType[];
    return filters.some((filter) => Object.values(columns)?.some((col) => col?.property === filter?.property));
  });

  const canSaveFilters = computed<boolean>(
    () => didActiveFiltersChange.value && showHeaders && showSaveBtn && hasAppliedFilterChanged(),
  );

  // Views
  const defaultView = computed<UserView>(() => views.value?.find((view) => view.isDefault));
  const appliedView = computed<UserView>(() => views.value?.find((view) => view.isApplied));
  // #endregion

  // #region Functions
  function updateAvailableFilters() {
    // TODO isDefault having an issue upon first load when no filters are saved.
    availableFilters.value = columnsAvailableForFiltering.value.map((c) => ({
      label: c.label,
      property: c.property,
      type: c.type,
      source: c.source,
      sourceFilterProperty: c.sourceFilterProperty,
      sourceLabel: c.sourceLabel,
      sourceID: c.sourceID,
      checkForEmptyness: c.checkForEmptyness,
      isDefault:
        (c.isDefault && !c.isFixed && !!defaultView.value) || // avoid displaying isDefault filters if the user saved an empty filter sequence
        c.isFixed, // filter is always default even if the user saved an empty filter sequence
      sendTracking: c.sendTracking,
      numericalOperatorsToExclude: c.numericalOperatorsToExclude,
      filterOptionsTransformation: c.filterOptionsTransformation,
      customQueryParams: c.customQueryParams,
    }));
  }

  function onTableFilterChanged(filter: TableFilterType) {
    const existingFilter = activeFilters.value.find((x) => x.property === filter.property);
    switch (filter.type) {
      case 'BooleanFilter':
      case 'DateFilter':
      case 'Filter':
      case 'ListFilter':
      case 'NumericFilter':
      case 'UUIDFilter': {
        Object.entries(filter).forEach(([key, value]) => {
          existingFilter.instance[key] = value;
        });
        break;
      }
      default:
        throw new Exception('Not implemented');
    }

    if (existingFilter.sendTracking) {
      GoogleAnalyticsApi.sendEvent('Filter', { category: category, value: filter.label });
    }

    fireFiltersChanged();
    serializeFiltersInURL();
  }

  function removeFilter(filter: TableFilterType) {
    const activeFilter = activeFilters.value.find((x) => x.property === filter.property);
    const hadValue = !activeFilter?.instance?.isEmpty;
    if (activeFilter && hadValue) {
      activeFilter.instance.clear();
    }
    if (activeFilter.isDefault) {
      // avoid default filters to be automatically reselected when they are being removed by the user
      activeFilter.isDefault = false;
    }
    activeFilters.value = activeFilters.value.filter((x) => x.property !== filter.property);
    selectedFilters.value = selectedFilters.value.filter((x) => x.property !== filter.property);

    if (!selectedFilters.value?.length) {
      // the user has purposefully removed all selected filter, avoid re-selecting the default ones without the user consent
      availableFilters.value = availableFilters.value.map((f) => ({
        ...f,
        isDefault: false,
      }));
    }

    if (hadValue) {
      fireFiltersChanged();
    }
    didActiveFiltersChange.value = true;

    serializeFiltersInURL();
  }

  function onFiltersCleared() {
    didActiveFiltersChange.value = true;
  }

  function onFilterSelectionChanged(e) {
    const removed = selectedFilters.value.filter((x) => !e.included.includes(x));
    removed.filter((x) => x.instance != null && x.instance.clear != null).forEach((x) => x.instance.clear()); // Reset removed filters filter.

    selectedFilters.value = e.included;
    didActiveFiltersChange.value = true;
    // In the case where options are removed or we're adding a boolean filter, we should trigger the filters changed event
    if (e.included.length === 0 || removed.length > 0 || e.included.some((x) => x.type === 'boolean')) {
      fireFiltersChanged();
      serializeFiltersInURL();
    }
  }

  function toggleComparisonPreviousPeriod() {
    showComparison.value = !showComparison.value;
    emits('toggleComparison', showComparison.value);
  }

  function fireFiltersChanged() {
    emits('filtersChanged');
  }

  function restoreSerializedFiltersFromUrl() {
    if (!isMainTable) return false;
    //
    // Parse url params
    //
    const searchParams = new URLSearchParams(window.location.search);
    const filters = searchParams.get('filters');
    return restoreSerializedFilters(filters);
  }

  function restoreSerializedFiltersFromLocalStorage(pushToUrl = false) {
    if (defaultView.value) {
      restoreSerializedFilters(defaultView.value.value);
    }
    if (pushToUrl && defaultView.value) {
      serializeFiltersInURL();
    }
  }

  function serializeFiltersInURL() {
    serializeFiltersInURLThrottledTask.value.run();
  }

  async function serializeFiltersInURLInternal() {
    if (!isMainTable) return;

    const serializedFilters = getSerializedFiltersString();

    try {
      await router.push({
        path: router.currentRoute.value.path,
        query: serializedFilters.length > 0 ? { filters: serializedFilters } : { filters: undefined },
      });
    } catch (error) {
      console.error('Error serializing filters in URL:', error);
    }
  }

  function getSerializedFiltersString(forUrl = true) {
    let serializedFilters = activeFilters.value.map((x) => x.instance.serializeForQueryString());

    // Only serialize filterText when serializing for the URL
    if (forUrl) {
      let serializedFilterText = filterText.value;
      if (serializedFilterText != null) {
        // If text filter contains any "|" it will be removed from the text filter
        serializedFilterText = serializedFilterText.replace(/\|/g, lightVerticalBar);
      }
      // filterText is not concatenated in the url if it is null (To avoid having something like filters=filtertText="some text")
      serializedFilters = serializedFilters.concat(
        filterText.value !== '' && filterText.value != null ? `filterText=${serializedFilterText}` : '',
      );
    }

    return serializedFilters.filter((x) => x != null).join('|');
  }

  function clearFilterText() {
    filterText.value = null;
    fireFiltersChanged();
  }

  function getFilters() {
    const filterCollection = new FilterCollection();
    if (fixedFilters != null && fixedFilters.length > 0) {
      fixedFilters.forEach((f) => {
        filterCollection.filters.push(f);
      });
    }

    if (filterText.value != null && filterText.value.trim() !== '') {
      const properties =
        textSearchProperties != null ? textSearchProperties : columnsFiltrableByText.value.map((x) => x.property);

      if (properties.length > 0) {
        const filterGroup = new FilterGroup([]);
        properties.forEach((prop) => {
          filterGroup.filters.push(
            new Filter(prop, [filterText.value], FilterComparisonOperator.LIKE, FilterLogicalOperator.OR),
          );
        });
        filterCollection.filters.push(filterGroup);
      }
    }

    if (activeFilters.value != null) {
      activeFilters.value.forEach((filter) => {
        const { instance: filterInstance } = filter;
        const filters = filterInstance instanceof Filter ? [filterInstance] : filterInstance.getFilters();
        filters.forEach((f) => {
          const existingFilter = filterCollection.filters.find(
            (x) =>
              x instanceof Filter &&
              x.property !== undefined &&
              f.property !== undefined &&
              x.property === f.property &&
              x.comparisonOperator === f.comparisonOperator,
          ) as Filter;
          if (existingFilter === undefined) {
            if (!(f instanceof FilterGroup && f.filters.length === 0)) {
              filterCollection.filters.push(f);
            }
          } else {
            f.values.forEach((v) => existingFilter.values.push(v));
          }
        });
      });
    }

    return filterCollection;
  }

  async function getFilterInstance(f: ITableFilter) {
    if (f.type === TableColumnTypes.list || f.source != null) {
      const listFilter = new ListFilter(
        f.property,
        f.label,
        [],
        [],
        f.source,
        f.sourceFilterProperty,
        f.sourceLabel,
        f.sourceID,
        false,
        f.customQueryParams,
      );
      await waitUntil(() => listFilter.isLoadingOptions === false);
      return listFilter;
    }
    switch (f.type) {
      case TableColumnTypes.boolean: {
        return new BooleanFilter(f.property, f.label, true, f.checkForEmptyness);
      }
      case TableColumnTypes.date:
      case TableColumnTypes.datetime: {
        return new DateFilter(f.property, f.label);
      }
      case TableColumnTypes.string: {
        return new Filter(
          f.property,
          [],
          FilterComparisonOperator.LIKE,
          FilterLogicalOperator.OR,
          f.label,
          f.customQueryParams,
        );
      }
      case TableColumnTypes.float:
      case TableColumnTypes.integer:
      case TableColumnTypes.currency:
      case TableColumnTypes.percentage:
        return new NumericFilter(f.type, f.property, f.label, null, null, null, f.numericalOperatorsToExclude);
      case TableColumnTypes.uuid:
        return new UUIDFilter(f.property, f.label);
      default:
        return new Filter(f.property, [], FilterComparisonOperator.LIKE, FilterLogicalOperator.OR, f.label);
    }
  }

  function getFilterInstanceType(f) {
    if (f == null) throw new Exception('Invalid filter');

    if (f.type === TableColumnTypes.list || f.source != null) {
      return ListFilter;
    }

    switch (f.type) {
      case TableColumnTypes.boolean:
        return BooleanFilter;
      case TableColumnTypes.date:
      case TableColumnTypes.datetime:
        return DateFilter;
      case TableColumnTypes.string:
        return Filter;
      case TableColumnTypes.float:
      case TableColumnTypes.integer:
      case TableColumnTypes.currency:
      case TableColumnTypes.percentage:
        return NumericFilter;
      case TableColumnTypes.uuid:
        return UUIDFilter;
      default:
        return Filter;
    }
  }

  function restoreSerializedFilters(filters: string) {
    if (filters == null || filters === '') {
      activeFilters.value = [];
      selectedFilters.value = [];
      return false;
    }
    const serializedFilters = filters.split('|');
    if (filters.includes('filterText')) {
      const filterTextValue = filters.split('filterText=').pop(); // Get the text value of the filter

      if (filterTextValue === 'null') {
        filterText.value = '';
      } else {
        filterText.value = filterTextValue.replace(new RegExp(`${lightVerticalBar}`, 'g'), '|');
      }
    }

    //
    // Update modified filters
    //
    const existing = serializedFilters.filter((sf) => activeFilters.value.some((x) => x.property === sf.split('~')[0]));
    existing.forEach((sf) => {
      const activeFilter = activeFilters.value.find((x) => x.property === sf.split('~')[0]);
      if (activeFilter.instance.serializeForQueryString() !== sf) {
        const filterInstanceType = getFilterInstanceType(activeFilter);
        activeFilter.instance = filterInstanceType.deserializeFromQueryString(activeFilter, sf);
      }
    });

    //
    // Remove active filters no longer present in serialized filters.
    //
    activeFilters.value = activeFilters.value.filter((af) =>
      serializedFilters.some((sf) => sf.split('~')[0] === af.property),
    );

    //
    // Add missing filters.
    //
    const missing = serializedFilters.filter(
      (sf) => !activeFilters.value.map((af) => af.property).some((prop) => prop === sf.split('~')[0]),
    );
    const toAdd = missing
      .map((sf) => availableFilters.value.find((x) => x.property === sf.split('~')[0]))
      .filter((x) => x != null);
    toAdd.forEach((f, i) => {
      f.instance = getFilterInstanceType(f).deserializeFromQueryString(f, missing[i]);
    });
    activeFilters.value.push(...toAdd);

    //
    // Restore selected filters if necessary (when loading page)
    //
    if (
      !areArraysEqual(
        selectedFilters.value.map((x) => x.property),
        activeFilters.value.map((x) => x.property),
      )
    ) {
      selectedFilters.value = availableFilters.value.filter((x) =>
        activeFilters.value.some((af) => af.property === x.property),
      );
    }

    fireFiltersChanged();

    return true;
  }

  function onTextFilterChanged() {
    textFilterChangedThrottledTask.value.run();
  }

  function onTextFilterChangedInternal() {
    emits('textSearchTermChanged', filterText.value);
    serializeFiltersInURL();
  }

  // Views

  function applyView(view: UserView) {
    if (!view) return;
    tableSettings.userViewService.applyView(view);
    restoreSerializedFilters(view.value);
    clearTooltips();
    didActiveFiltersChange.value = false;
  }

  function onViewSaveClicked() {
    if (appliedView.value) {
      showUpdateUserViewModal.value = true;
    } else {
      openAddFilterModal();
    }
  }

  function saveAsNewFilter() {
    showUpdateUserViewModal.value = false;
    openAddFilterModal();
  }

  function importView() {
    viewImportModalRef.value.open();
  }

  async function saveView({ name, isDefault = false }) {
    const newView = new UserView({
      name,
      isDefault,
      value: getSerializedFiltersString(),
    });
    await tableSettings?.userViewService.add(newView);
    resetAddViewModalParams();
  }

  async function updateView() {
    showUpdateUserViewModal.value = false;
    await tableSettings.userViewService.update(
      new UserView({
        ...appliedView.value,
        value: getSerializedFiltersString(false),
      }),
    );
    didActiveFiltersChange.value = false;
    GlobalEventBus.$emit(GlobalEvents.clearTooltips); // As the save button disappears, the tooltip is left behind.
  }

  function openAddFilterModal() {
    showAddUserViewModal.value = true;
  }

  function resetAddViewModalParams() {
    showAddUserViewModal.value = false;
  }

  function hasAppliedFilterChanged() {
    if (!activeFilters.value?.length) return false;

    const sanitizedActiveFilters =
      getSerializedFiltersString()
        .split('|')
        ?.filter((f) => f) ?? [];
    // if no user filter is applied, but there are active filters (meaning the user has started filtering), let them save
    if (!appliedView.value) return !!sanitizedActiveFilters?.length;
    const sanitizedAppliedFilters = appliedView.value.value.split('|')?.filter((f) => f) ?? [];
    return !areArraysEqual(orderBy(sanitizedAppliedFilters), orderBy(sanitizedActiveFilters));
  }

  function viewListener() {
    tableSettings.userViewService.views$.subscribe((updatedViews) => {
      views.value = updatedViews;
    });
  }

  function onViewCleared() {
    filtersDropdownRef.value.clear();
    tableSettings.userViewService.clearView();
  }

  // #endregion

  // #region Throttled Tasks
  const serializeFiltersInURLThrottledTask = ref<ThrottledTaskRunner<void>>();
  const textFilterChangedThrottledTask = ref<ThrottledTaskRunner<void>>();

  function setupThrottles() {
    serializeFiltersInURLThrottledTask.value = new ThrottledTaskRunner(() => serializeFiltersInURLInternal());
    textFilterChangedThrottledTask.value = new ThrottledTaskRunner(() => onTextFilterChangedInternal());
  }

  function destroyThrottles() {
    serializeFiltersInURLThrottledTask.value.destroy();
    textFilterChangedThrottledTask.value.destroy();
  }

  function selectFilters(filters: ITableFilter[]) {
    selectedFilters.value = filters;
  }
  // #endregion

  // #region Lifecycle Hooks
  onBeforeMount(() => {
    views.value = tableSettings.userViews;
    updateAvailableFilters();
  });

  onMounted(() => {
    setupThrottles();

    viewListener();
    const restoredFromURL = restoreSerializedFiltersFromUrl();

    // if the user access the page with filters in the URL, restore that. Otherwise, use their default view if there is one
    if (!restoredFromURL) {
      // apply default user filter
      applyView(defaultView.value);
    } else {
      // check if the serialized filters corresponds to a user view and apply it if it does
      const view = tableSettings.userViewService.getViewFromUrl();
      if (view) {
        applyView(view);
      }
    }

    if (isMainTable) {
      GlobalEventBus.$on(GlobalEvents.windowStatePopped, () => {
        restoreSerializedFiltersFromUrl();
      });
    }
  });

  onBeforeUnmount(() => {
    if (isMainTable) {
      GlobalEventBus.$off('windowStatePopped', () => {
        restoreSerializedFiltersFromUrl();
      });
    }
  });

  onUnmounted(() => {
    destroyThrottles();
  });
  // #endregion

  // #region Watchers
  watch(
    () => props.textSearch,
    (val) => {
      filterText.value = val;
      fireFiltersChanged();
    },
  );

  watch(filterText, () => onTextFilterChanged());

  watch(selectedFilters, (newValue, oldValue) => {
    let shouldFireFilterChangedEvent = false;
    const removed = oldValue.filter((o) => !newValue.includes(o));
    const added = newValue.filter((o) => !oldValue.includes(o));
    removed.forEach((f) => {
      const activeFilter = activeFilters.value.find((x) => x.property === f.property);
      if (activeFilter != null) {
        activeFilters.value.splice(activeFilters.value.indexOf(activeFilter), 1);
      }
    });
    added.forEach(async (f) => {
      // When loading the page activeFilters are restored from their serialized state, check if they are already present.
      if (!activeFilters.value.some((x) => x.property === f.property)) {
        if (f.instance == null) {
          f.instance = await getFilterInstance(f);
          if (f.instance instanceof BooleanFilter) {
            shouldFireFilterChangedEvent = true; // Boolean filters always default to 'Yes' when added.
          }
        }
        activeFilters.value.push(f);
      }
    });

    if (shouldFireFilterChangedEvent) {
      fireFiltersChanged();
      serializeFiltersInURL();
    }
  });
  // #endregion

  // #region Expose
  defineExpose({
    activeFilters,
    availableFilters,
    selectedFilters,
    isFiltered,
    didActiveFiltersChange,
    showComparison,
    filterText,
    tableFilterRefs,
    serializeFiltersInURL,
    restoreSerializedFilters,
    getSerializedFiltersString,
    getFilters,
    updateAvailableFilters,
    getFilterInstance,
    restoreSerializedFiltersFromUrl,
    selectFilters,
  });
  // #endregion
</script>
